import type { ConnectionState } from '@connaro/shared';
import { CONNECTION_STATES } from '@connaro/shared';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { ConnectionStateChip } from '../components/ConnectivityPanel';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface WgInterface {
  id: string;
  name: string;
  listenPort: number;
  addresses: string[];
  mtu: number | null;
  mssClamp: number | null;
  status: string;
}

interface WgKey {
  id: string;
  publicKey: string;
  status: string;
  generation: number;
  hasPresharedKey: boolean;
}

interface PeerHealth {
  id: string;
  peerPublicKey: string;
  handshakeAgeSeconds: number | null;
  healthy: boolean;
  rxBytes: string;
  txBytes: string;
  observedEndpoint: string | null;
  configuredEndpoint: string | null;
  endpointMatchesConfigured: boolean;
  connectionMethod: string | null;
  connectionState: string | null;
}

interface Consistency {
  consistent: boolean;
  issues: string[];
  configuredPeers: number;
  reportedPeers: number | null;
}

/** Byte counts arrive as strings because they can exceed Number.MAX_SAFE_INTEGER. */
function formatBytes(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = n;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? size : size.toFixed(1)} ${units[unit]}`;
}

function formatAge(seconds: number | null, never: string): string {
  if (seconds === null) return never;
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

const shortKey = (key: string): string => `${key.slice(0, 12)}…`;

export function WireGuardDetailPage(): JSX.Element {
  const t = useT();
  const { id = '' } = useParams();

  const iface = useAsync(() => api.get<WgInterface>(`/wireguard/interfaces/${id}`), [id]);
  const keys = useAsync(() => api.get<WgKey[]>(`/wireguard/interfaces/${id}/keys`), [id]);
  const health = useAsync(() => api.get<PeerHealth[]>(`/wireguard/interfaces/${id}/health`), [id]);
  const consistency = useAsync(
    () => api.get<Consistency>(`/wireguard/interfaces/${id}/consistency`),
    [id],
  );

  const reloadAll = (): void => {
    keys.reload();
    health.reload();
    consistency.reload();
  };

  const revoke = (keyId: string) => () => {
    const reason = window.prompt(t('wireguard.revokeReason'));
    if (!reason) return;
    void api.post(`/wireguard/keys/${keyId}/revoke`, { reason }).then(reloadAll);
  };

  if (iface.loading) return <Loading />;
  if (iface.error) return <ErrorState message={iface.error.message} onRetry={iface.reload} />;
  if (!iface.data) return <EmptyState hint={t('wireguard.empty')} />;

  return (
    <section>
      <div className="page-head">
        <h1>{iface.data.name}</h1>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() =>
            void api.post(`/wireguard/interfaces/${id}/keys/complete-rotation`).then(reloadAll)
          }
        >
          {t('wireguard.completeRotation')}
        </button>
      </div>

      <dl className="detail-grid">
        <dt>{t('wireguard.listenPort')}</dt>
        <dd>{iface.data.listenPort}</dd>
        <dt>{t('wireguard.addresses')}</dt>
        <dd>{iface.data.addresses.join(', ')}</dd>
        <dt>{t('wireguard.mtu')}</dt>
        <dd>{iface.data.mtu ?? '—'}</dd>
        <dt>{t('wireguard.mssClamp')}</dt>
        <dd>{iface.data.mssClamp ?? '—'}</dd>
        <dt>{t('wireguard.status')}</dt>
        <dd>{iface.data.status}</dd>
      </dl>

      <h2>{t('wireguard.consistency')}</h2>
      {consistency.data ? (
        <div
          className={
            consistency.data.consistent ? 'banner banner--success' : 'banner banner--warning'
          }
        >
          <p>
            {consistency.data.consistent ? t('wireguard.consistent') : t('wireguard.inconsistent')}
          </p>
          {consistency.data.issues.length > 0 ? (
            <ul>
              {consistency.data.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <h2>{t('wireguard.keys')}</h2>
      {!keys.data || keys.data.length === 0 ? (
        <EmptyState hint={t('states.empty')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('wireguard.generation')}</th>
                <th>{t('wireguard.publicKey')}</th>
                <th>{t('wireguard.status')}</th>
                <th>{t('wireguard.hasPresharedKey')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {keys.data.map((key) => (
                <tr key={key.id}>
                  <td>{key.generation}</td>
                  <td>
                    <code>{shortKey(key.publicKey)}</code>
                  </td>
                  <td>
                    <span className={`chip chip--${key.status.toLowerCase()}`}>{key.status}</span>
                  </td>
                  <td>{key.hasPresharedKey ? '✓' : '—'}</td>
                  <td className="row-actions">
                    {key.status === 'REVOKED' ? null : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--danger"
                        onClick={revoke(key.id)}
                      >
                        {t('wireguard.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>{t('wireguard.health')}</h2>
      {!health.data || health.data.length === 0 ? (
        <EmptyState hint={t('wireguard.noPeers')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('wireguard.publicKey')}</th>
                <th>{t('wireguard.lastHandshake')}</th>
                <th>{t('wireguard.transfer')}</th>
                <th>{t('wireguard.observedEndpoint')}</th>
                <th>{t('connection.state')}</th>
                <th>{t('wireguard.status')}</th>
              </tr>
            </thead>
            <tbody>
              {health.data.map((peer) => (
                <tr key={peer.id}>
                  <td>
                    <code>{shortKey(peer.peerPublicKey)}</code>
                  </td>
                  <td>{formatAge(peer.handshakeAgeSeconds, t('wireguard.neverHandshaked'))}</td>
                  <td>
                    ↓ {formatBytes(peer.rxBytes)} / ↑ {formatBytes(peer.txBytes)}
                  </td>
                  <td>
                    {peer.observedEndpoint ?? '—'}
                    {!peer.endpointMatchesConfigured && peer.configuredEndpoint ? (
                      <span className="chip chip--warning">{peer.configuredEndpoint}</span>
                    ) : null}
                  </td>
                  <td>
                    {/* Null until NAT traversal plans this peering; a peering on
                        its static config simply shows no traversal state. */}
                    {peer.connectionState &&
                    (CONNECTION_STATES as readonly string[]).includes(peer.connectionState) ? (
                      <ConnectionStateChip state={peer.connectionState as ConnectionState} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <span className={`chip chip--${peer.healthy ? 'success' : 'warning'}`}>
                      {peer.healthy ? t('wireguard.healthy') : t('wireguard.unhealthy')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
